// SF3 — Peak Hours and Days.
//
// Every bucket is IST. Amazon stamps orders in UTC, five and a half hours
// behind, so an order placed at 23:30 IST would otherwise be counted on the
// previous day and the two views would disagree with each other.
//
// A bucket with no orders renders as a zero, not as a blank. Nobody buying at
// 4am is information; an empty cell would read as missing data.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getPeakTimes } from '../services/orderAnalyticsApi';

// Orders first: the PRD's default, because this surface answers when demand
// arrives rather than what it was worth.
const METRICS = [
  { key: 'orders', label: 'Orders' },
  { key: 'units', label: 'Units' },
  { key: 'revenue', label: 'Revenue' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const format = (value, metric) => {
  if (value === null || value === undefined) return '—';
  return metric === 'revenue' ? `₹${inr.format(Math.round(value))}` : inr.format(value);
};

const SHORT_DAY = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed',
  Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

// A horizontal bar chart. Hand-drawn rather than pulling in a chart library for
// two lists of fixed length.
const Bars = ({ buckets, metric }) => {
  const widest = Math.max(...buckets.map((b) => b[metric] || 0), 0);
  return (
    <div className={local.bars}>
      {buckets.map((bucket) => (
        <div key={bucket.key} className={local.barRow}>
          <span className={local.barLabel}>
            {SHORT_DAY[bucket.label] || bucket.label}
          </span>
          <span className={local.barTrack}>
            <span
              className={bucket.is_peak ? local.barFillPeak : local.barFill}
              style={{ width: widest ? `${Math.max((bucket[metric] / widest) * 100, 0.5)}%` : '0%' }}
            />
          </span>
          <span className={local.barValue}>{format(bucket[metric], metric)}</span>
          {bucket.is_peak && <span className={local.peakTag}>peak</span>}
        </div>
      ))}
    </div>
  );
};

const Suppressed = ({ reason }) => (
  <div className={local.suppressed}>{reason}</div>
);

const PeakTimes = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [metric, setMetric] = useState('orders');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getPeakTimes(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const grid = data?.day_hour || [];
  const busiestCell = grid.length
    ? Math.max(...grid.map((row) => Math.max(...row))) : 0;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>When orders arrive</h2>
        <div className={local.headAside}>
          <span className={local.istTag}>IST</span>
          <div className={local.segmented}>
            {METRICS.map((option) => (
              <button key={option.key} type="button"
                className={option.key === metric ? local.segmentOn : local.segment}
                onClick={() => setMetric(option.key)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className={local.muted}>
        The rhythm of your own order book, by day of week and hour of day.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load order timing'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && data && (
        <>
          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>By day of week</h3>
          </div>
          {data.day_view.available
            ? <Bars buckets={data.by_day} metric={metric} />
            : <Suppressed reason={data.day_view.reason} />}

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>By hour of day</h3>
          </div>
          {data.hour_view.available
            ? <Bars buckets={data.by_hour} metric={metric} />
            : <Suppressed reason={data.hour_view.reason} />}

          {grid.length > 0 && (
            <>
              <div className={local.groupHead}>
                <h3 className={local.groupTitle}>Day against hour, on orders</h3>
              </div>
              <div className={local.tableScroll}>
                <div className={local.heatmap}>
                  <div className={local.heatRow}>
                    <span className={local.heatCorner} />
                    {Array.from({ length: 24 }, (_, hour) => (
                      <span key={hour} className={local.heatHour}>
                        {hour % 3 === 0 ? hour : ''}
                      </span>
                    ))}
                  </div>
                  {grid.map((row, day) => (
                    <div key={data.by_day[day].label} className={local.heatRow}>
                      <span className={local.heatDay}>
                        {SHORT_DAY[data.by_day[day].label]}
                      </span>
                      {row.map((value, hour) => (
                        <span
                          key={hour}
                          className={local.heatCell}
                          title={`${data.by_day[day].label} ${String(hour).padStart(2, '0')}:00 — ${value} orders`}
                          style={{
                            // Zero stays visible as an empty cell rather than
                            // disappearing into the background.
                            background: value
                              ? `rgba(31,111,235,${0.08 + (value / busiestCell) * 0.82})`
                              : '#f7f8f9',
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {data.peak?.provisional && (
            <div className={local.provisional}>{data.peak.reason}</div>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default PeakTimes;
